import { useMutation } from '@tanstack/react-query';
import React, { useCallback } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';

import ErrorToastContent from 'common/components/ErrorToastContent';
import { updateOffer } from 'modules/shared-menu/api';
import useAssetRepositoryUpload from 'modules/shared-menu/hooks/useAssetRepositoryUpload/useAssetRepositoryUpload';
import { Offer } from 'modules/shared-menu/pages/SharedMenuEditorPage/types';

import FailedImagesList from './FailedImagesList';
import { ThumbnailImage } from './types';
import { useUploadToast } from './useUploadToast';
import { cleanFileName, getNextPossibleUploadIndex, validateDroppedFiles } from './utils';
import { ITEM_MAX_IMAGE_COUNT } from '../../constants';

type UseImagesMutationProps = {
  existingImageUrls: string[];
  offer: Offer;
  menuId: string;
};

type UseImagesHandlerProps = {
  images: ThumbnailImage[];
  onUpdateMenuData: (offer: Offer) => void;
  offer: Offer;
  menuId: string;
};

type UpdatePayload = {
  offer: Offer;
  imageUrls: string[];
};

const getChangedImagesIndices = (existingImageUrls: string[], imageUrls: string[], startingIndex: number = 0) => {
  return imageUrls.reduce((changedIndices, url, index) => {
    if (existingImageUrls[index + startingIndex] !== url) {
      return [...changedIndices, index];
    }

    return changedIndices;
  }, []);
};

const useImagesMutation = ({ existingImageUrls, offer, menuId }: UseImagesMutationProps) => {
  const [changingImages, setChangingImages] = React.useState<number[]>([]);
  const { uploadAssets } = useAssetRepositoryUpload();

  const { mutateAsync: handleUpdateImages } = useMutation(
    ['offer-images-update', offer.id],
    async ({ offer, imageUrls }: UpdatePayload) => {
      const { id, scope, version, ...updatePayload } = {
        ...offer,
        image: imageUrls.length > 0 ? imageUrls[0] : undefined,
        images: imageUrls.length > 0 ? imageUrls.map(url => ({ url })) : undefined,
      };
      const updatedOffer = await updateOffer(menuId, offer.id, updatePayload);
      return updatedOffer;
    },
    {
      onMutate: ({ imageUrls }: UpdatePayload) => {
        if (existingImageUrls.length < imageUrls.length) {
          setChangingImages(getChangedImagesIndices(existingImageUrls, imageUrls));
        } else {
          setChangingImages(getChangedImagesIndices(imageUrls, existingImageUrls));
        }
      },
      onSettled: () => {
        setChangingImages([]);
      },
      onError: (error: any) => {
        toast.error(<ErrorToastContent error={error} />);
      },
    },
  );

  const { mutateAsync: handleUploadImages } = useMutation(
    ['offer-images-upload', offer.id],
    async ({ validFiles, uploadAt = 0 }: { validFiles: File[]; uploadAt?: number }) => {
      if (validFiles.length === 0) {
        return offer;
      }

      const correctedUploadIndex = getNextPossibleUploadIndex(uploadAt, existingImageUrls.length);

      setChangingImages(validFiles.reduce((indices, _, index) => [...indices, index + correctedUploadIndex], []));

      const imageFiles = validFiles.map(file => new File([file], cleanFileName(file.name), { type: file.type }));
      const { publishedImagesUrls: newImageUrls } = await uploadAssets(imageFiles);

      const imageUrls = existingImageUrls;
      imageUrls.splice(correctedUploadIndex, newImageUrls.length, ...newImageUrls);

      const updatedOffer = await handleUpdateImages({ offer, imageUrls });
      return updatedOffer;
    },
    {
      onSettled: () => {
        setChangingImages([]);
      },
      onError: (error: any) => {
        toast.error(<ErrorToastContent error={error} />);
      },
    },
  );

  const { mutateAsync: handleDeleteImage } = useMutation(
    ['offer-images-delete', offer.id],
    async ({ deleteAt }: { deleteAt: number }) => {
      if (deleteAt < 0 || deleteAt >= existingImageUrls.length) {
        return offer;
      }

      const imageUrls = existingImageUrls;
      imageUrls.splice(deleteAt, 1);

      const updatedOffer = await handleUpdateImages({ offer, imageUrls });
      return updatedOffer;
    },
    {
      onError: (error: any) => {
        toast.error(<ErrorToastContent error={error} />);
      },
    },
  );

  return {
    uploadNewImages: handleUploadImages,
    deleteImage: handleDeleteImage,
    updateImages: handleUpdateImages,
    changingImages,
  };
};

export const useImagesHandler = ({ images, onUpdateMenuData, offer, menuId }: UseImagesHandlerProps) => {
  const { showUploadToast } = useUploadToast();

  const { uploadNewImages, deleteImage, updateImages, changingImages } = useImagesMutation({
    existingImageUrls: images.map(image => image.url),
    offer,
    menuId,
  });

  const handleFileDrop = useCallback(
    async (files: File[], index: number | null = null) => {
      const isExceedsMaxImages =
        index === null
          ? images.length + files.length > ITEM_MAX_IMAGE_COUNT
          : Math.min(images.length, index) + files.length > ITEM_MAX_IMAGE_COUNT;

      if (isExceedsMaxImages) {
        toast.error(
          <FormattedMessage
            id="image-upload.listHeaderUpload.errors.over-max-amount"
            values={{ count: ITEM_MAX_IMAGE_COUNT }}
          />,
        );
        return;
      }

      const { fileDropResult, fileErrors, validFiles } = await validateDroppedFiles(files);

      if (!(fileDropResult === 'success' || fileDropResult === 'partial-success')) {
        toast.error(<FailedImagesList errors={fileErrors} />);
        return;
      }

      const updatedOffer = await uploadNewImages({ validFiles, uploadAt: index ?? images.length });
      onUpdateMenuData(updatedOffer);

      showUploadToast({
        fileDropResult,
        validFiles,
        fileErrors,
        onUndo: async () => {
          const revertedOffer = await updateImages({
            offer: updatedOffer,
            imageUrls: images.map(image => image.url),
          });

          onUpdateMenuData(revertedOffer);
        },
      });
    },
    [images, onUpdateMenuData, uploadNewImages, updateImages, showUploadToast],
  );

  const handleImageDelete = useCallback(
    async (deleteAt: number) => {
      const updatedOffer = await deleteImage({ deleteAt });
      onUpdateMenuData(updatedOffer);
    },
    [deleteImage, onUpdateMenuData],
  );

  return {
    handleFileDrop,
    handleImageDelete,
    changingImages,
    updateImages,
  };
};
